import { CurrencyOrEmpty } from '@plato/app-shared/components/currency-or-empty';
import { DeltaTriangle } from '@plato/ui-legacy/components/typography/delta-triangle';
import { NumberOrEmpty } from '@plato/ui-legacy/components/typography/number-or-empty';
import { PercentageOrEmpty } from '@plato/ui-legacy/components/typography/percentage-or-empty';
import { safeDeltaPercentage } from '@plato/ui-legacy/lib/utils/safe-delta-percentage';
import type { CustomTooltipProps } from '@tremor/react';
import { CellType, DetailedChartTooltip } from './detailed-chart-tooltip.tsx';

export type ValueDisplayType = 'currency' | 'number';

// The chart row behind one bucket. Recharts hands it to us as `any` on every series
// entry, so we assert the shape once instead of casting field by field. `thisYear` is
// typed as present because the `thisYearSeries` guard below already stands in for that
// check -- future months carry no current-year value.
type TooltipBucket = {
    month: string;
    year: number;
    lastYear: number;
    thisYear: number;
};

export type YearComparisonChartTooltipProps = {
    label: string;
    differenceLabel: string;
    valueDisplayType?: ValueDisplayType;
    hideAmounts?: boolean;
} & CustomTooltipProps;

export function YearComparisonChartTooltip({
    payload,
    active,
    label,
    differenceLabel,
    valueDisplayType = 'currency',
    hideAmounts = false,
}: YearComparisonChartTooltipProps) {
    if (!active || !payload) {
        return null;
    }

    const [firstSeries, thisYearSeries] = payload;

    if (!firstSeries) {
        return null;
    }

    // `payload` has one entry per line on the chart, and every entry's `.payload` is the same
    // hovered row. We take it from the first entry because that is the one guarded above --
    // the current-year line is absent for future months. The row carries its own year
    // because a trailing-twelve-month window spans two calendar years.
    const bucket = firstSeries.payload as TooltipBucket;

    const monthString = bucket.month;
    const bucketYear = bucket.year;
    const lastYearValue = bucket.lastYear;
    const thisYearValue = thisYearSeries ? bucket.thisYear : 0;
    const delta = thisYearValue - lastYearValue;
    const deltaPercentage = safeDeltaPercentage(thisYearValue, lastYearValue);
    const isPositive = delta > 0 ? true : delta < 0 ? false : null;

    const ValueComponent = valueDisplayType === 'number' ? NumberOrEmpty : CurrencyOrEmpty;
    const amountMask = '*****';

    return (
        <DetailedChartTooltip>
            <DetailedChartTooltip.Row>
                <DetailedChartTooltip.Cell cellType={CellType.LABEL}>{label}</DetailedChartTooltip.Cell>
            </DetailedChartTooltip.Row>
            <DetailedChartTooltip.Row>
                <DetailedChartTooltip.Cell>{`${monthString} ${bucketYear}`}</DetailedChartTooltip.Cell>
                <DetailedChartTooltip.Cell>
                    {hideAmounts ? amountMask : <ValueComponent>{thisYearValue}</ValueComponent>}
                </DetailedChartTooltip.Cell>
            </DetailedChartTooltip.Row>
            <DetailedChartTooltip.Row>
                <DetailedChartTooltip.Cell>{`${monthString} ${bucketYear - 1}`}</DetailedChartTooltip.Cell>
                <DetailedChartTooltip.Cell>
                    {hideAmounts ? amountMask : <ValueComponent>{lastYearValue}</ValueComponent>}
                </DetailedChartTooltip.Cell>
            </DetailedChartTooltip.Row>
            <DetailedChartTooltip.Row borderTop>
                <DetailedChartTooltip.Cell>{differenceLabel}</DetailedChartTooltip.Cell>
                <DetailedChartTooltip.Cell>
                    {hideAmounts ? amountMask : <ValueComponent>{delta}</ValueComponent>}
                </DetailedChartTooltip.Cell>
            </DetailedChartTooltip.Row>
            <DetailedChartTooltip.Row>
                <DetailedChartTooltip.Cell
                    cellType={CellType.DELTA}
                    isPositive={isPositive}
                    className="w-full text-right"
                >
                    <DeltaTriangle className="mr-1" up={isPositive} />
                    <PercentageOrEmpty showEmptyIfZero>{deltaPercentage}</PercentageOrEmpty>
                </DetailedChartTooltip.Cell>
            </DetailedChartTooltip.Row>
        </DetailedChartTooltip>
    );
}
